import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import walletAdminService, { WalletStats, WalletSystemStats, UserWallet } from '../services/walletAdminService';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import moment from 'moment-jalaali';
import './EntityManagement.css';

const formatMoney = (amount?: number): string =>
  `${toPersianDigits((amount || 0).toLocaleString('en-US'))} تومان`;

const formatCount = (n?: number): string =>
  toPersianDigits((n || 0).toLocaleString('en-US'));

const formatJalali = (date?: string): string => {
  if (!date) return '-';
  try {
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD HH:mm'));
  } catch {
    return String(date);
  }
};

type TabKey = 'stats' | 'operations' | 'search';

const WalletAdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('stats');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<WalletStats | null>(null);
  const [systemStats, setSystemStats] = useState<WalletSystemStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User wallet lookup
  const [searchUserId, setSearchUserId] = useState('');
  const [userWallet, setUserWallet] = useState<UserWallet | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Dialog states
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const [deductDialogOpen, setDeductDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Form states
  const [formUserId, setFormUserId] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formOrderNumber, setFormOrderNumber] = useState('');
  // کلید idempotent هر بار بازشدن دیالوگ (دوبار کلیک/تکرار درخواست ⇒ فقط یک بار اعمال می‌شود)
  const [formIdemKey, setFormIdemKey] = useState('');
  const newIdemKey = () => (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `k${Date.now()}${Math.random().toString(36).slice(2, 8)}`);
  const errMsg = (err: any, fallback: string) => err?.message || err?.response?.data?.message || fallback;
  const validAmount = (v: string) => Number.isInteger(Number(v)) && Number(v) > 0;

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, systemData] = await Promise.all([
        walletAdminService.getStatistics(),
        walletAdminService.getSystemStatistics()
      ]);
      setStats(statsData);
      setSystemStats(systemData);
    } catch (err) {
      setError('خطا در بارگذاری آمار کیف پول');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Auto-dismiss success toast
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const handleSearchUser = async () => {
    if (!searchUserId.trim()) return;
    setSearchLoading(true);
    setError(null);
    try {
      const wallet = await walletAdminService.getUserWallet(searchUserId.trim());
      setUserWallet(wallet);
    } catch (err: any) {
      setError(errMsg(err, 'کاربر یافت نشد'));
      setUserWallet(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddBonus = async () => {
    if (!formUserId || !formAmount) return;
    if (!validAmount(formAmount)) { setDialogError('مبلغ باید عدد صحیح مثبت (تومان) باشد'); return; }
    setDialogLoading(true);
    setDialogError(null);
    try {
      await walletAdminService.addBonus(formUserId, Number(formAmount), formDescription, formIdemKey);
      setSuccess('بونوس با موفقیت اضافه شد');
      setBonusDialogOpen(false);
      resetForm();
      loadStats();
      if (userWallet?.userId === formUserId) handleSearchUser();
    } catch (err: any) {
      setDialogError(errMsg(err, 'خطا در افزودن بونوس'));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeduct = async () => {
    if (!formUserId || !formAmount || !formReason) return;
    if (!validAmount(formAmount)) { setDialogError('مبلغ باید عدد صحیح مثبت (تومان) باشد'); return; }
    setDialogLoading(true);
    setDialogError(null);
    try {
      await walletAdminService.deductFromWallet(formUserId, Number(formAmount), formReason);
      setSuccess('مبلغ با موفقیت کسر شد');
      setDeductDialogOpen(false);
      resetForm();
      loadStats();
      if (userWallet?.userId === formUserId) handleSearchUser();
    } catch (err: any) {
      setDialogError(errMsg(err, 'خطا در کسر مبلغ'));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleManualRefund = async () => {
    if (!formUserId || !formAmount || !formReason) return;
    if (!validAmount(formAmount)) { setDialogError('مبلغ باید عدد صحیح مثبت (تومان) باشد'); return; }
    setDialogLoading(true);
    setDialogError(null);
    try {
      const res: any = await walletAdminService.manualRefund(formUserId, Number(formAmount), formReason, formOrderNumber.trim() || undefined, formIdemKey);
      setSuccess(res?.message || 'بازپرداخت با موفقیت انجام شد');
      setRefundDialogOpen(false);
      resetForm();
      loadStats();
      if (userWallet?.userId === formUserId) handleSearchUser();
    } catch (err: any) {
      setDialogError(errMsg(err, 'خطا در بازپرداخت'));
    } finally {
      setDialogLoading(false);
    }
  };

  const resetForm = () => {
    setFormUserId('');
    setFormAmount('');
    setFormDescription('');
    setFormReason('');
    setFormOrderNumber('');
    setDialogError(null);
  };

  const openDialog = (kind: 'bonus' | 'deduct' | 'refund') => {
    resetForm();
    setFormIdemKey(newIdemKey());
    if (kind === 'bonus') setBonusDialogOpen(true);
    else if (kind === 'deduct') setDeductDialogOpen(true);
    else setRefundDialogOpen(true);
  };

  const totalWallets = systemStats?.totalWallets || systemStats?.totalUsers || 0;
  const activeBalance = systemStats?.totalBalance || 0;
  const todayTransactions = systemStats?.recentTransactions || 0;
  // مسدود = قفل مرجوعی + رزرو برداشت‌های تأییدشده (داخل موجودی کل است)
  const frozenBalance = (systemStats?.lockedBalance || 0) + (systemStats?.blockedForWithdrawal || 0);

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت کیف پول</h1>
          <p className="entity-subtitle">مشاهده آمار و مدیریت عملیات کیف پول کاربران</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn refresh"
            onClick={loadStats}
            disabled={loading}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {error && (
        <div
          className="dialog-error"
          style={{ margin: '0 0 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit' }}
          >
            ✕
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            margin: '0 0 12px',
            padding: '12px 16px',
            background: '#dcfce7',
            color: '#166534',
            border: '1px solid #86efac',
            borderRadius: 8,
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Stats Cards (always visible at top) */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><AccountBalanceWalletIcon /></div>
          <div>
            <p className="entity-stat-label">کل کیف پول‌ها</p>
            <p className="entity-stat-value">{loading ? '...' : formatCount(totalWallets)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><PaidIcon /></div>
          <div>
            <p className="entity-stat-label">مجموع موجودی کیف پول‌ها (بدهی پلتفرم)</p>
            <p className="entity-stat-value">{loading ? '...' : formatMoney(activeBalance)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><TrendingUpIcon /></div>
          <div>
            <p className="entity-stat-label">تراکنش‌های ۲۴ ساعت اخیر</p>
            <p className="entity-stat-value">{loading ? '...' : formatCount(todayTransactions)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">قفل‌شده + رزرو برداشت</p>
            <p className="entity-stat-value">{loading ? '...' : formatMoney(frozenBalance)}</p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: 6,
          background: 'var(--color-bg-default)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        {([
          { key: 'stats', label: 'آمار', Icon: AssessmentIcon },
          { key: 'operations', label: 'عملیات', Icon: SettingsIcon },
          { key: 'search', label: 'جستجو', Icon: PersonSearchIcon },
        ] as Array<{ key: TabKey; label: string; Icon: React.ElementType }>).map(({ key, label, Icon }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                flex: '1 1 140px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 14px',
                borderRadius: 8,
                border: active ? '1px solid var(--color-primary)' : '1px solid transparent',
                background: active ? 'var(--color-bg-paper)' : 'transparent',
                color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              }}
            >
              <Icon fontSize="small" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <>
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                padding: 48,
                background: 'var(--color-bg-paper)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 10,
              }}
            >
              <CircularProgress />
              <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 13 }}>در حال بارگذاری...</p>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 14,
                marginBottom: 16,
              }}
            >
              {/* System Breakdown */}
              <div
                style={{
                  background: 'var(--color-bg-paper)',
                  border: '1px solid var(--color-divider)',
                  borderRadius: 10,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    background: 'var(--color-bg-default)',
                    borderBottom: '1px solid var(--color-divider)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <GroupIcon fontSize="small" /> آمار کلی سیستم
                </div>
                <div style={{ padding: '4px 12px' }}>
                  {[
                    { label: 'تعداد کل کیف پول‌ها', value: formatCount(systemStats?.totalWallets) },
                    { label: 'کیف پول‌های فعال / غیرفعال', value: `${formatCount(systemStats?.activeWallets)} / ${formatCount(systemStats?.inactiveWallets)}` },
                    { label: 'کیف پول‌های دارای موجودی', value: formatCount(systemStats?.walletsWithBalance) },
                    { label: 'کیف پول‌های منفی (نیاز به بررسی)', value: formatCount(systemStats?.negativeWallets) },
                    { label: 'مجموع موجودی سیستم', value: formatMoney(systemStats?.totalBalance) },
                    { label: 'قابل خرج (منهای قفل و رزرو)', value: formatMoney(systemStats?.availableBalance) },
                    { label: 'قفل‌شده (مرجوعی)', value: formatMoney(systemStats?.lockedBalance) },
                    { label: 'رزرو برداشت‌های در جریان', value: formatMoney(systemStats?.blockedForWithdrawal) },
                    { label: 'میانگین موجودی', value: formatMoney(systemStats?.averageBalance) },
                    { label: 'کل واریزی / کل خرج‌شده', value: `${formatMoney(systemStats?.totalEarned)} / ${formatMoney(systemStats?.totalSpent)}` },
                  ].map((row, i, arr) => (
                    <div
                      key={row.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 4px',
                        borderBottom: i < arr.length - 1 ? '1px solid var(--color-divider)' : 'none',
                      }}
                    >
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Withdrawal Breakdown */}
              <div
                style={{
                  background: 'var(--color-bg-paper)',
                  border: '1px solid var(--color-divider)',
                  borderRadius: 10,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    background: 'var(--color-bg-default)',
                    borderBottom: '1px solid var(--color-divider)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <ReceiptLongIcon fontSize="small" /> آمار برداشت‌ها
                </div>
                <div style={{ padding: '4px 12px' }}>
                  {[
                    { label: 'کل درخواست‌ها', value: formatCount(stats?.totalRequests), color: 'var(--color-text-primary)' },
                    { label: 'در انتظار تأیید', value: `${formatCount(stats?.pendingRequests)} — ${formatMoney(stats?.pendingAmount)}`, color: '#ea580c' },
                    { label: 'تأییدشده / در حال پردازش (رزرو)', value: `${formatCount((stats?.approvedRequests || 0) + (stats?.processingRequests || 0))} — ${formatMoney(stats?.reservedAmount)}`, color: '#2563eb' },
                    { label: 'تکمیل شده (پرداخت‌شده)', value: `${formatCount(stats?.completedRequests)} — ${formatMoney(stats?.completedAmount)}`, color: '#16a34a' },
                    { label: 'رد شده / لغوشده', value: `${formatCount(stats?.rejectedRequests)} / ${formatCount(stats?.cancelledRequests)}`, color: '#dc2626' },
                  ].map((row, i, arr) => (
                    <div
                      key={row.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 4px',
                        borderBottom: i < arr.length - 1 ? '1px solid var(--color-divider)' : 'none',
                      }}
                    >
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Operations Tab */}
      {activeTab === 'operations' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {/* Bonus Card */}
          <div
            style={{
              background: 'var(--color-bg-paper)',
              border: '1px solid var(--color-divider)',
              borderRadius: 10,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: '#dcfce7',
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AddCircleOutlineIcon />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  افزودن جایزه
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  اعطای بونوس یا اعتبار به کیف پول کاربر
                </p>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              این عملیات مبلغی را به عنوان جایزه به موجودی کاربر اضافه می‌کند و تراکنش در سابقه ثبت می‌شود.
            </p>
            <button
              onClick={() => openDialog('bonus')}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <AddCircleOutlineIcon fontSize="small" /> افزودن جایزه
            </button>
          </div>

          {/* Deduct Card */}
          <div
            style={{
              background: 'var(--color-bg-paper)',
              border: '1px solid var(--color-divider)',
              borderRadius: 10,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: '#fee2e2',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <RemoveCircleOutlineIcon />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  کسر مبلغ
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  برداشت اداری از کیف پول کاربر
                </p>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              مبلغ مشخص از موجودی کاربر کسر می‌شود. ثبت دلیل الزامی است و در سابقه نگهداری می‌شود.
            </p>
            <button
              onClick={() => openDialog('deduct')}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#dc2626',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <RemoveCircleOutlineIcon fontSize="small" /> کسر مبلغ
            </button>
          </div>

          {/* Refund Card */}
          <div
            style={{
              background: 'var(--color-bg-paper)',
              border: '1px solid var(--color-divider)',
              borderRadius: 10,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: '#FCE8EE',
                  color: '#B25373',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <UndoIcon />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  بازگشت وجه
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  بازپرداخت دستی به کیف پول کاربر
                </p>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              بازپرداخت مبلغ مربوط به یک سفارش یا رویداد به کیف پول کاربر. امکان ثبت شماره سفارش وجود دارد.
            </p>
            <button
              onClick={() => openDialog('refund')}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#B25373',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <UndoIcon fontSize="small" /> بازگشت وجه
            </button>
          </div>
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <>
          <div className="entity-filters">
            <div className="filters-row">
              <div className="filter-search-group" style={{ maxWidth: 520 }}>
                <input
                  className="filter-search"
                  type="text"
                  placeholder="شناسه کاربر را وارد کنید..."
                  value={searchUserId}
                  onChange={(e) => setSearchUserId(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearchUser(); }}
                />
                <button
                  className="filter-search-btn"
                  onClick={handleSearchUser}
                  disabled={searchLoading || !searchUserId.trim()}
                >
                  {searchLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <SearchIcon fontSize="small" />}
                </button>
              </div>
            </div>
          </div>

          {userWallet ? (
            <div
              style={{
                background: 'var(--color-bg-paper)',
                border: '1px solid var(--color-divider)',
                borderRadius: 10,
                overflow: 'hidden',
              }}
            >
              <div
                className="edit-card"
                style={{ border: 'none' }}
              >
                <div className="edit-card-header" style={{
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  background: 'var(--color-bg-default)',
                  borderBottom: '1px solid var(--color-divider)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <AccountBalanceWalletIcon fontSize="small" /> اطلاعات کیف پول کاربر
                </div>
                <div className="edit-card-body" style={{ padding: '20px 24px' }}>
                  <div className="edit-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>شناسه کاربر</label>
                      <div className="cell-code" style={{ padding: '8px 10px', fontSize: 12, wordBreak: 'break-all' }}>
                        {userWallet.userId}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>کاربر</label>
                      <div style={{ padding: '8px 0', fontSize: 13, fontWeight: 700 }}>
                        {userWallet.user?.name || '—'}{userWallet.user?.phoneNumber ? ` · ${toPersianDigits(userWallet.user.phoneNumber)}` : ''}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>موجودی کل</label>
                      <div style={{ padding: '8px 0', fontSize: 14, fontWeight: 700, color: '#B25373' }}>
                        {formatMoney(userWallet.summary?.balance)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>قابل خرج</label>
                      <div style={{ padding: '8px 0', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                        {formatMoney(userWallet.summary?.availableBalance)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>قفل‌شده / رزرو برداشت</label>
                      <div style={{ padding: '8px 0', fontSize: 14, fontWeight: 700, color: '#ea580c' }}>
                        {formatMoney(userWallet.summary?.lockedBalance)} / {formatMoney(userWallet.summary?.blockedForWithdrawal)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>کل واریزی / کل خرج‌شده</label>
                      <div style={{ padding: '8px 0', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                        {formatMoney(userWallet.summary?.totalEarned)} / {formatMoney(userWallet.summary?.totalSpent)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>کل برداشت شده</label>
                      <div style={{ padding: '8px 0', fontSize: 14, fontWeight: 700, color: '#0ea5e9' }}>
                        {formatMoney(userWallet.summary?.totalWithdrawn)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>تاریخ ایجاد</label>
                      <div className="cell-code" style={{ padding: '8px 10px' }}>
                        {formatJalali(userWallet.createdAt || undefined)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>آخرین بروزرسانی</label>
                      <div className="cell-code" style={{ padding: '8px 10px' }}>
                        {formatJalali(userWallet.summary?.lastTransactionAt || userWallet.updatedAt || undefined)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>گردش کامل</label>
                      <div style={{ padding: '8px 0' }}>
                        <a href={`/finance/statements?userId=${userWallet.userId}`} style={{ fontSize: 12, color: 'var(--color-primary)' }}>مشاهده گردش حساب کیف پول</a>
                      </div>
                    </div>
                  </div>

                  {/* Quick action shortcuts */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
                    <button
                      onClick={() => {
                        setFormUserId(userWallet.userId);
                        openDialog('bonus');
                        setFormUserId(userWallet.userId);
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid #86efac',
                        background: '#f0fdf4',
                        color: '#166534',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <AddCircleOutlineIcon fontSize="small" /> افزودن جایزه
                    </button>
                    <button
                      onClick={() => {
                        setFormUserId(userWallet.userId);
                        openDialog('deduct');
                        setFormUserId(userWallet.userId);
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid #fca5a5',
                        background: '#fef2f2',
                        color: '#991b1b',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" /> کسر مبلغ
                    </button>
                    <button
                      onClick={() => {
                        setFormUserId(userWallet.userId);
                        openDialog('refund');
                        setFormUserId(userWallet.userId);
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid #EBB3C2',
                        background: '#FFF4F7',
                        color: '#8E3D59',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <UndoIcon fontSize="small" /> بازگشت وجه
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: 40,
                background: 'var(--color-bg-paper)',
                border: '1px dashed var(--color-border)',
                borderRadius: 10,
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              <PersonSearchIcon style={{ fontSize: 40, opacity: 0.5, marginBottom: 8 }} />
              <p style={{ margin: 0 }}>برای مشاهده اطلاعات کیف پول، شناسه کاربر را وارد کنید.</p>
            </div>
          )}
        </>
      )}

      {/* Add Bonus Dialog */}
      <Dialog
        open={bonusDialogOpen}
        onClose={() => !dialogLoading && setBonusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="wallet-bonus-dialog"
      >
        <div className="edit-dialog-title">
          <span>افزودن جایزه به کیف پول</span>
          <button className="details-close" onClick={() => setBonusDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        {dialogError && <div className="dialog-error">{dialogError}</div>}
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات جایزه</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>شناسه کاربر <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formUserId}
                      onChange={(e) => setFormUserId(e.target.value)}
                      placeholder="شناسه کاربر را وارد کنید..."
                    />
                  </div>
                  <div className="edit-field full">
                    <label>مبلغ (تومان) <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="number"
                      value={formAmount}
                      onChange={(e) => setFormAmount(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>توضیحات</label>
                    <textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="توضیحات جایزه (اختیاری)"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setBonusDialogOpen(false)} disabled={dialogLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            style={{ background: '#16a34a' }}
            onClick={handleAddBonus}
            disabled={dialogLoading || !formUserId || !formAmount}
          >
            {dialogLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CheckCircleOutlineIcon fontSize="small" />}
            افزودن جایزه
          </button>
        </div>
      </Dialog>

      {/* Deduct Dialog */}
      <Dialog
        open={deductDialogOpen}
        onClose={() => !dialogLoading && setDeductDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="wallet-deduct-dialog"
      >
        <div className="edit-dialog-title">
          <span>کسر مبلغ از کیف پول</span>
          <button className="details-close" onClick={() => setDeductDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        {dialogError && <div className="dialog-error">{dialogError}</div>}
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات کسر</div>
              <div className="edit-card-body">
                <div
                  style={{
                    padding: 12,
                    background: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: 8,
                    marginBottom: 16,
                    fontSize: 12,
                    color: '#991b1b',
                    lineHeight: 1.6,
                  }}
                >
                  توجه: این عملیات مبلغ مشخص را از موجودی کاربر کسر می‌کند و در سابقه ثبت می‌شود.
                </div>
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>شناسه کاربر <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formUserId}
                      onChange={(e) => setFormUserId(e.target.value)}
                      placeholder="شناسه کاربر را وارد کنید..."
                    />
                  </div>
                  <div className="edit-field full">
                    <label>مبلغ (تومان) <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="number"
                      value={formAmount}
                      onChange={(e) => setFormAmount(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>دلیل <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea
                      value={formReason}
                      onChange={(e) => setFormReason(e.target.value)}
                      placeholder="دلیل کسر از کیف پول را وارد کنید..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDeductDialogOpen(false)} disabled={dialogLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            style={{ background: '#dc2626' }}
            onClick={handleDeduct}
            disabled={dialogLoading || !formUserId || !formAmount || !formReason}
          >
            {dialogLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CheckCircleOutlineIcon fontSize="small" />}
            کسر مبلغ
          </button>
        </div>
      </Dialog>

      {/* Manual Refund Dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => !dialogLoading && setRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="wallet-refund-dialog"
      >
        <div className="edit-dialog-title">
          <span>بازگشت وجه به کیف پول</span>
          <button className="details-close" onClick={() => setRefundDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        {dialogError && <div className="dialog-error">{dialogError}</div>}
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات بازگشت وجه</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>شناسه کاربر <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formUserId}
                      onChange={(e) => setFormUserId(e.target.value)}
                      placeholder="شناسه کاربر را وارد کنید..."
                    />
                  </div>
                  <div className="edit-field full">
                    <label>مبلغ (تومان) <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="number"
                      value={formAmount}
                      onChange={(e) => setFormAmount(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>دلیل <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea
                      value={formReason}
                      onChange={(e) => setFormReason(e.target.value)}
                      placeholder="دلیل بازپرداخت..."
                      rows={3}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>شماره سفارش (اختیاری)</label>
                    <input
                      type="text"
                      value={formOrderNumber}
                      onChange={(e) => setFormOrderNumber(e.target.value)}
                      placeholder="شماره سفارش مرتبط..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setRefundDialogOpen(false)} disabled={dialogLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            style={{ background: '#B25373' }}
            onClick={handleManualRefund}
            disabled={dialogLoading || !formUserId || !formAmount || !formReason}
          >
            {dialogLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CheckCircleOutlineIcon fontSize="small" />}
            بازگشت وجه
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default WalletAdminPage;
